import { DwSecurityContext } from "@devwareapps/devware-cap";
import { DwClientSessionInfo } from "./models/dw-client-session-info.model";

export class DwClientSessionUtilService {

    /**
     * Build a complete, serializable snapshot of the current client session.
     * Intended for realtime / websocket diagnostics and logging.
     */
    public static buildSessionInfo(securityContext: DwSecurityContext, connectionAttempt: number = 1): DwClientSessionInfo {
        const ua = navigator.userAgent || '';
        const user = securityContext?.ApplicationUser;
        const appUser = securityContext?.ApplicationUser;
        const emlutaing = user?.UserId != appUser?.UserId;

        const sessionInfo: DwClientSessionInfo = {
            SessionId: this.getOrCreateSessionId(),

            UserId: user.UserId,
            UserFullName: user.FirstNameLastName,

            Emulating: emlutaing,
            Device: {
                Type: this.detectDeviceType(ua),
                OS: this.detectOS(ua),
                Browser: this.detectBrowser(ua),
                UserAgent: ua
            },

            Client: {
                Language: navigator.language,
                TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                Screen: `${screen.width}x${screen.height}`,
                Viewport: `${window.innerWidth}x${window.innerHeight}`
            },

            Connection: {
                StartedUtc: new Date().toISOString(),
                Url: window.location.href,
                Attempt: connectionAttempt
            }
        };

        if (emlutaing) {
            sessionInfo.EmulatingUserId = appUser.UserId;
            sessionInfo.EmluatingUserFullName = appUser.FirstNameLastName;
        }

        sessionInfo.ClientInfoString = this.buildClientInfoString(sessionInfo);

        return sessionInfo;
    }

    private static buildClientInfoString(clientSessionInfo: DwClientSessionInfo) {
        const infoParts = [
            `UserId=${clientSessionInfo.UserId}`,
            `Name=${clientSessionInfo.UserFullName}`,
            `Device=${clientSessionInfo.Device.Type}`,
            `OS=${clientSessionInfo.Device.OS}`,
            `Browser=${clientSessionInfo.Device.Browser}`,
            `SessionId=${clientSessionInfo.SessionId}`,
        ];

        if (clientSessionInfo.Emulating) {
            infoParts.push(`EmulatingUserId=${clientSessionInfo.EmulatingUserId}`);
            infoParts.push(`EmulatingUserFullName=${clientSessionInfo.EmluatingUserFullName}`);
        }

        return infoParts.join('; ');
    }

    // =========================
    // Helpers
    // =========================

    private static getOrCreateSessionId(): string {
        const key = 'dw.client.sessionId';
        let id = sessionStorage.getItem(key);

        if (!id) {
            id = this.generateUuid();
            sessionStorage.setItem(key, id);
        }

        return id;
    }

    public static generateUuid(): string {
        // Prefer crypto.randomUUID when available (runtime-safe)
        const cryptoAny = crypto as any;
        if (cryptoAny?.randomUUID) {
            return cryptoAny.randomUUID();
        }

        // RFC4122 v4 fallback
        let d = new Date().getTime();
        let d2 = (performance && performance.now && performance.now() * 1000) || 0;

        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            let r = Math.random() * 16;
            if (d > 0) {
                r = (d + r) % 16 | 0;
                d = Math.floor(d / 16);
            } else {
                r = (d2 + r) % 16 | 0;
                d2 = Math.floor(d2 / 16);
            }
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    }

    private static detectDeviceType(
        ua: string
    ): 'Desktop' | 'Tablet' | 'Phone' | 'Unknown' {

        const agent = ua.toLowerCase();

        if (/ipad/.test(agent)) return 'Tablet';
        if (/iphone|ipod/.test(agent)) return 'Phone';
        if (/android/.test(agent) && !/mobile/.test(agent)) return 'Tablet';
        if (/android/.test(agent)) return 'Phone';
        if (/mobile/.test(agent)) return 'Phone';

        return 'Desktop';
    }

    private static detectOS(userAgent: string): string {

        // Preferred: User-Agent Client Hints
        const uaData = (navigator as any).userAgentData;
        if (uaData?.platform) {
            return uaData.platform; // e.g. "Windows", "macOS", "Android", "iOS"
        }

        // Fallback: UA string parsing (still required for Safari/iOS)
        const ua = userAgent.toLowerCase();

        if (/iphone|ipad|ipod/.test(ua)) return 'iOS';
        if (/android/.test(ua)) return 'Android';
        if (/windows/.test(ua)) return 'Windows';
        if (/mac os/.test(ua)) return 'macOS';
        if (/linux/.test(ua)) return 'Linux';

        return 'Unknown';
    }

    private static detectBrowser(ua: string): string {
        if (/edg/i.test(ua)) return 'Edge';
        if (/chrome/i.test(ua) && !/edg/i.test(ua)) return 'Chrome';
        if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'Safari';
        if (/firefox/i.test(ua)) return 'Firefox';

        return 'Unknown';
    }
}